import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import Footer from "@/components/Footer";
import FloatingContactBar from "@/components/FloatingContactBar";
import {
  Phone,
  MessageSquare,
  ShieldCheck,
  Clock,
  Settings,
  Thermometer,
  Box,
  Zap,
  CheckCircle2,
  Tag,
  Award,
  Users,
  MapPin,
  Plus,
  Check,
  X,
  BadgeCheck,
  BadgeAlert,
} from "lucide-react";

const BANNER_IMAGE =
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1778735283/Artboard_16_lzojmj.webp";

const MOBILE_HERO_TOP_IMAGE =
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1778762420/Artboard_21_hppiun.webp";

const MOBILE_APPLICATIONS_IMAGE =
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1778762419/Artboard_23_eu3nim.webp";

const MOBILE_BOTTOM_GALLERY_IMAGE =
  "https://res.cloudinary.com/dizdlq9fo/image/upload/v1778762420/Artboard_22_evvsxa.webp";

const GALLERY_TOP = [
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735278/Artboard_3_dhrih2.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735277/Artboard_4_jorri4.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735278/Artboard_5_wh7ofi.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735278/Artboard_6_ti8buu.webp",
];

const GALLERY_APPLICATIONS = [
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735694/Artboard_19_copy_iqa0ta.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735694/Artboard_18_ojh77q.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735694/Artboard_17_bwh8qb.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778747453/Artboard_19_kd2ibp.webp",
];

const GALLERY_BOTTOM = [
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735278/Artboard_13_rprx0j.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735278/Artboard_7_liqjwi.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735278/Artboard_9_evuy1u.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735278/Artboard_14_bogoqd.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735278/Artboard_8_satcjv.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735278/Artboard_12_kd2rpz.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778735279/Artboard_11_fewmey.webp",
  "https://res.cloudinary.com/dizdlq9fo/image/upload/f_auto,q_auto,w_800/v1778748127/Artboard_15_hwtqzn.webp",
];

const partners = [
  { name: "SML", style: "text-2xl md:text-4xl font-black text-white" },
  { name: "Larsen & Toubro", style: "text-xl md:text-3xl font-light text-white/40" },
  { name: "Lamprell", style: "text-2xl md:text-4xl font-bold text-white/70" },
  { name: "Archidon", style: "text-xl md:text-3xl font-medium text-red-600/80" },
  { name: "Shoba Construction", style: "text-2xl md:text-4xl font-semibold text-white/50" },
];

const specs = [
  { label: "Structure", value: "High-Strength Aluminium Profile" },
  { label: "Clear Span Width", value: "5m to 60m (Customizable)" },
  { label: "Eave Height", value: "2.5m to 12.7m (Customizable)" },
  { label: "Roof Cover", value: "850 GSM White Soft PVC" },
  { label: "Side Cover", value: "850 GSM White Soft PVC | ABS Panel | Sandwich Panel" },
  { label: "Wind Resistance", value: "Up to 145 KPH (ASCEC-16)" },
  { label: "Temperature Control", value: "Maintain Stable Temperature with Air Conditioning" },
];

const chooseUsFeatures = [
  { icon: ShieldCheck, title: "Industrial Strength", desc: "High-grade aluminum structure with PVC fabric built to last." },
  { icon: Settings, title: "Customizable Dimensions", desc: "Available in any size to suit your storage requirements." },
  { icon: Clock, title: "Quick Installation", desc: "Fast-track installation saves time and reduces operational downtime." },
  { icon: Zap, title: "Weather Resistant", desc: "Designed to withstand high winds, rain, and extreme heat." },
  { icon: Thermometer, title: "Temperature Control Options", desc: "Insulation, ventilation & cooling solutions available." },
  { icon: Box, title: "Cost Effective Solution", desc: "Lower cost compared to permanent construction." },
];

const applications = [
  {
    title: "Construction Labour Camps",
    desc: "Comfortable labour rest area tents for construction workers and project site accommodation."
  },
  {
    title: "Staff Dining Areas",
    desc: "Temporary dining and cafeteria tents for workforce meal and break areas."
  },
  {
    title: "Industrial Workforce Shelters",
    desc: "Heavy-duty labour tents for industrial facilities and factory worker resting zones."
  },
  {
    title: "Oil & Gas Workforce Camps",
    desc: "Durable labour accommodation tents designed for remote oil & gas project locations."
  },
  {
    title: "Mining & Infrastructure Projects",
    desc: "Temporary labour rest shelters for infrastructure and mining operations across UAE."
  },
  {
    title: "Government Workforce Facilities",
    desc: "Scalable labour camp tents for government and large-scale public sector projects."
  }
];

const TrustBar = () => {
  const items = [
    { icon: Award, title: "30+", sub: "Years Experience" },
    { icon: Users, title: "8000+", sub: "Projects Delivered" },
    { icon: MapPin, title: "UAE-Wide", sub: "Fast Installation" },
    { icon: BadgeAlert, title: "CICPA", sub: "Compliant Tents" },
    { icon: ShieldCheck, title: "Civil Defense", sub: "Compliant Tents" },
    { icon: BadgeCheck, title: "MOHRE", sub: "Compliant Tents" },
    
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:flex md:flex-nowrap items-start md:items-center gap-4 md:gap-6 mt-6 border-t border-white/10 pt-6">
      {items.map((item, i) => (
        <div
  key={i}
  className={`flex items-start md:items-center gap-3 ${
  i === 3 ? "md:ml-4" : ""
}`}
>
          <div className="p-2 bg-purple-500/10 rounded-lg shrink-0">
            <item.icon className="w-5 h-5 text-[#bd6aff]" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-white font-bold text-sm leading-none whitespace-nowrap">{item.title}</span>
            <span className="text-zinc-200 text-[10px] uppercase tracking-wider font-bold mt-1.5">{item.sub}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const Warehousing = () => {
  const navigate = useNavigate();
  const quoteRef = useRef<HTMLDivElement>(null);
  const [geo, setGeo] = useState({ code: "+971", country: "AE" });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    requirements: "",
  });
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    document.title = "Labour Rest Area UAE | Premium Rest Area Solutions Dubai | BA Tents";

    const metaDescription = document.querySelector('meta[name="description"]');

    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "BA Tents is a leading warehouse tent manufacturer in UAE providing industrial warehouse tents, storage tents, and temporary warehouse solutions for sale and rental. Fast installation, durable PVC structures, and customized warehouse shelters across UAE."
      );
    }

    const mediaQuery = window.matchMedia("(min-width: 768px)");

    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
    };

    setIsDesktop(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);

    window.scrollTo(0, 0);

    const controller = new AbortController();

    const geoTimeout = setTimeout(() => {
      fetch("https://ipapi.co/json/", {
        signal: controller.signal,
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.country_calling_code) {
            const formattedCode = data.country_calling_code.startsWith("+")
              ? data.country_calling_code
              : `+${data.country_calling_code}`;

            setGeo({
              code: formattedCode,
              country: data.country_code,
            });
          }
        })
        .catch(() => {});
    }, 3000);

    return () => {
      controller.abort();
      clearTimeout(geoTimeout);
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  const handleMiniSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    navigate("/thankyou");

    const SCRIPT_URL =
      "https://script.google.com/macros/s/AKfycby5HKEZNMObZRaeTidKlOMCLR_2YpgZuP2jOscP-OHWz2pi8PT0j8giTglCwm0VO5_J/exec";

    fetch(SCRIPT_URL, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...formData,
        phone: `${geo.code} ${formData.phone}`,
      }),
    }).catch(() => {});
  };

  const scrollToQuote = useCallback(() => {
    quoteRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, []);

  return (
    <PageLayout>
      <div className="min-h-screen bg-black text-white">
        <section className="relative min-h-[60vh] md:min-h-[90vh] w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] -mt-28 md:-mt-12 overflow-hidden flex flex-col bg-black">
          <img
            src={BANNER_IMAGE}
            className="hidden md:block absolute inset-0 w-full h-full object-cover z-0 opacity-60 scale-105"
            alt="Industrial Labour Rest Area Tents UAE"
            style={{ objectPosition: "center top" }}
            width={1920}
            height={1080}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />

          <div className="absolute inset-0 hidden md:block bg-gradient-to-b from-black/40 via-transparent to-black z-[1]" />

          <div className="flex-1 relative z-10 flex flex-col items-center justify-center px-4 md:px-0">
            <div className="w-full max-w-[1440px] grid grid-cols-1 md:grid-cols-2 gap-8 items-center md:px-12">
              <div className="w-full mb-6 p-4 md:p-0 md:-ml-12 bg-transparent">
                <div className="mt-24 inline-block px-3 py-1 rounded-full border border-white/20 bg-white/10 mb-4">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-white">
                    Leading <span className="text-purple-400">Labour Tent Manufacturer in UAE</span>
                  </span>
                </div>

                <h1 className="text-[32px] md:text-[58px] font-black text-white leading-[1.1] mb-5 tracking-tight uppercase">
                  Labour Rest <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#bd6aff] to-purple-400">
                    Area Tents
                  </span>{" "}
                  <br />
                  <span className="text-[22px] md:text-[38px] text-white/90">in UAE & Dubai</span>
                </h1>

                <div className="mb-8 max-w-2xl">
                  <p className="hidden md:block text-zinc-300 text-sm md:text-base font-normal leading-relaxed">
                    <span className="text-white font-bold">BA Tents</span> offers labour tents on rental and labour tents on sale providing workforce accommodation tents, shaded rest tents, and temporary labour camp solutions across UAE.
                  </p>

                  <p className="block md:hidden text-zinc-300 text-sm font-normal leading-relaxed">
                    <span className="text-white font-bold">BA Tents</span> offers labour tents on rental and labour tents on sale providing workforce accommodation tents, shaded rest tents, and temporary labour camp solutions across UAE.
                  </p>
                </div>

                <div className="flex flex-row items-center gap-1 md:gap-2 -mt-2 mb-3 md:mb-6 w-full max-w-lg">
                  <button
                    onClick={scrollToQuote}
                    className="flex-1 py-4 bg-purple-500/10 md:bg-gradient-to-r from-[#8b31ff] to-[#bd6aff] border border-purple-400/40 md:border-0 rounded-xl text-white font-black text-[10px] md:text-xs tracking-wider flex items-center justify-center gap-1.5 uppercase backdrop-blur-md md:backdrop-blur-0 shadow-none md:shadow-[0_0_20px_rgba(139,49,255,0.3)] active:scale-95 transition-transform"
                  >
                    <Phone className="w-3.5 h-3.5 fill-black/20" />
                    <span>Get Quote</span>
                  </button>

                  <a
                    href="tel:+971588243660"
                    className="flex-1 py-4 bg-white/10 md:bg-white border border-white/20 md:border-0 rounded-xl text-white md:text-black font-black text-[10px] md:text-xs flex items-center justify-center gap-1.5 uppercase backdrop-blur-md md:backdrop-blur-0 active:scale-95 transition-transform"
                  >
                    <Phone className="w-3.5 h-3.5 fill-white md:fill-black/20 text-white md:text-black" />
                    <span>Call Now</span>
                  </a>

                  <a
                    href="https://wa.me/971588243660"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-4 bg-emerald-500/10 md:bg-emerald-500 border border-emerald-500/40 md:border-0 rounded-xl text-emerald-400 md:text-white font-black text-[10px] md:text-xs flex items-center justify-center gap-1.5 uppercase backdrop-blur-md md:backdrop-blur-0 active:scale-95 transition-transform"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    <span>WhatsApp</span>
                  </a>
                </div>

                <div className="max-w-lg">
                  <TrustBar />
                </div>

                {!isDesktop && (
                  <div className="relative w-full mt-8 -mb-9 overflow-hidden rounded-2xl bg-zinc-900 border border-white/10 shadow-2xl">
                    <img
                      src={MOBILE_HERO_TOP_IMAGE}
                      className="w-full h-auto object-cover"
                      alt="Warehouse tents mobile gallery"
                      width={800}
                      height={1000}
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                    />
                  </div>
                )}
              </div>

              <div className="hidden md:block" />
            </div>
          </div>
        </section>

        <section className="hidden md:block mt-12 w-full max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-4 gap-1">
            {GALLERY_TOP.map((img, idx) => (
              <div key={`top-${idx}`} className="aspect-[4/3] overflow-hidden rounded-lg border border-white/10 bg-zinc-900">
                <img
                  src={img}
                  loading="lazy"
                  decoding="async"
                  width={600}
                  height={450}
                  className="w-full h-full object-cover transition-all duration-500"
                  alt={`Warehouse Grid ${idx}`}
                />
              </div>
            ))}
          </div>
        </section>

        <section className="py-16 bg-black relative overflow-hidden border-t border-white/5">
          <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-black to-transparent z-10" />
          <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-black to-transparent z-10" />
          <div className="flex flex-col gap-4">
            <p className="text-center text-[10px] font-black text-zinc-300 uppercase tracking-[0.3em] mb-4">
              Trusted by Industry Leaders
            </p>

            <div className="flex overflow-hidden group">
              <div className="flex flex-nowrap gap-16 items-center whitespace-nowrap px-8 animate-[marquee_30s_linear_infinite]">
                {[...partners, ...partners].map((partner, idx) => (
                  <span key={idx} className={`${partner.style} tracking-tighter opacity-60 hover:opacity-100 transition-opacity uppercase`}>
                    {partner.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="pt-4 pb-8 md:py-12 max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-4 items-stretch">
            {/* Match the exact charcoal card wrapper from the image */}
            <div className="lg:col-span-2 relative p-6 md:p-8 rounded-[2rem] bg-[#121212] border border-zinc-800/60 shadow-2xl flex flex-col justify-between">
              
              <div>
                {/* Title matches the style of the image headers */}
                <h2 className="text-xl font-black mb-6 md:mb-8 uppercase tracking-tighter text-white">
                  Technical <span className="text-purple-500">Specifications</span>
                </h2>

                {/* Clean, minimalist table layout */}
                <table className="w-full text-left text-xs border-collapse">
                  <tbody>
                    {specs.map((s, i) => (
                      <tr key={i} className="border-b border-zinc-800/50 last:border-0">
                        {/* Left Column: Muted label text */}
                        <td className="py-4 font-bold text-zinc-400 uppercase text-[9px] tracking-widest w-1/3">
                          {s.label}
                        </td>
                        {/* Right Column: Clean white data text matching the image's list style */}
                        <td className="py-4 text-zinc-200 font-medium text-sm">
                          {s.value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          

            <div 
              ref={quoteRef} 
              className="relative p-6 md:p-8 rounded-3xl bg-[#0F0F0F] border-2 border-purple-900/60 shadow-[0_0_30px_rgba(139,49,255,0.15)] flex flex-col gap-4 overflow-hidden"
            >
              
              {/* Static Subtle Header Border (Matches the image's clean lines) */}
              <div className="flex flex-col gap-1 border-b-2 border-zinc-900 pb-5">
                <h2 className="text-xl font-bold text-white tracking-tighter uppercase">Quick Quote</h2>
                <span className="text-[9px] font-black text-purple-400 uppercase tracking-widest">
                  Immediate 15-Minute Response
                </span>
              </div>

              <form className="space-y-4 pt-2" onSubmit={handleMiniSubmit}>
                <input
                  type="text"
                  placeholder="Full Name"
                  required
                  className="w-full bg-[#121212] border border-zinc-800 rounded-xl px-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />

                <input
                  type="email"
                  placeholder="Email Address"
                  required
                  className="w-full bg-[#121212] border border-zinc-800 rounded-xl px-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />

                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-400 text-[10px] font-black border-r border-zinc-800 pr-3">
                    {geo.code}
                  </div>
                  <input
                    type="tel"
                    required
                    placeholder="Mobile Number"
                    className="w-full bg-[#121212] border border-zinc-800 rounded-xl pl-16 pr-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>

                <textarea
                  placeholder="Tell us about your project requirements..."
                  className="w-full h-28 bg-[#121212] border border-zinc-800 rounded-xl px-4 py-3.5 text-zinc-200 text-[11px] focus:outline-none resize-none focus:border-purple-600 transition-colors placeholder:text-zinc-500"
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                />

                <button
                  type="submit"
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-purple-800 text-white font-black rounded-xl uppercase tracking-widest text-[11px] shadow-[0_10px_30px_rgba(139,49,255,0.2)] active:scale-95 transition-all hover:brightness-110"
                >
                  Send Request
                </button>
              </form>
            </div>
          </div>
        </section>

        <section className="pt-8 pb-16 md:py-20 max-w-7xl mx-auto px-6 border-t border-white/5">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-black tracking-tight uppercase">
              Ideal for Workforce & <span className="text-[#bd6aff]">Site Zones</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              {applications.map((app, i) => (
                <div key={i} className="flex gap-4 group">
                  <div className="mt-1">
                    <CheckCircle2 className="text-[#bd6aff] w-5 h-5 shrink-0" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-sm uppercase tracking-wide mb-1 group-hover:text-[#bd6aff] transition-colors">
                      {app.title}
                    </h3>
                    <p className="text-zinc-300 text-[11px] leading-relaxed max-w-md">{app.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden md:grid grid-cols-2 gap-3">
              {GALLERY_APPLICATIONS.map((img, idx) => (
                <div key={`app-img-${idx}`} className="relative overflow-hidden rounded-xl border border-white/5 bg-zinc-900 shadow-2xl aspect-[16/10]">
                  <img
                    src={img}
                    loading="lazy"
                    decoding="async"
                    width={600}
                    height={450}
                    className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out hover:scale-105"
                    alt="Industrial Application"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-80" />
                </div>
              ))}
            </div>

            <div className="block md:hidden overflow-hidden rounded-xl border border-white/10 bg-zinc-900 shadow-2xl">
              <img
                src={MOBILE_APPLICATIONS_IMAGE}
                className="w-full h-auto object-cover"
                alt="Industrial applications mobile gallery"
                width={800}
                height={1000}
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>

          <div className="text-center mb-12 mt-[80px]">
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-4 uppercase">
              Why Choose Our Labour Rest Area Tents?
            </h2>
            <p className="text-zinc-300 text-sm max-w-2xl mx-auto uppercase tracking-widest">
              Industry leading standards in temporary storage
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {chooseUsFeatures.map((f, i) => (
              <div key={i} className="p-6 bg-zinc-900/50 border border-white/5 rounded-2xl flex flex-col items-center text-center hover:bg-zinc-900 transition-colors group">
                <f.icon className="w-8 h-8 text-[#bd6aff] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-[11px] font-bold mb-2 tracking-tight uppercase text-white">{f.title}</h3>
                <p className="text-zinc-300 text-[10px] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="pb-24 max-w-7xl mx-auto px-2 md:px-6 space-y-2">
          <div className="hidden md:block space-y-2">
            <div className="grid grid-cols-4 gap-2">
              {GALLERY_BOTTOM.slice(0, 4).map((img, idx) => (
                <div key={`bottom-1-${idx}`} className="aspect-[4/3] overflow-hidden rounded-xl border border-white/10">
                  <img
                    src={img}
                    loading="lazy"
                    decoding="async"
                    width={600}
                    height={450}
                    className="w-full h-full object-cover transition-all duration-700"
                    alt="Warehouse Project"
                  />
                </div>
              ))}
            </div>

            <div className="grid grid-cols-4 gap-2">
              {GALLERY_BOTTOM.slice(4, 8).map((img, idx) => (
                <div key={`bottom-2-${idx}`} className="aspect-[4/3] overflow-hidden rounded-xl border border-white/10">
                  <img
                    src={img}
                    loading="lazy"
                    decoding="async"
                    width={600}
                    height={450}
                    className="w-full h-full object-cover transition-all duration-700"
                    alt="Warehouse Installation"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="block md:hidden overflow-hidden rounded-xl border border-white/10 bg-zinc-900">
            <img
              src={MOBILE_BOTTOM_GALLERY_IMAGE}
              className="w-full h-auto object-cover"
              alt="Warehouse projects mobile gallery"
              width={800}
              height={1000}
              loading="lazy"
              decoding="async"
            />
          </div>
        </section>

        <section className="pb-24 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="relative group p-[1px] rounded-3xl overflow-hidden bg-white/5 border border-white/10 flex flex-col">
              <div className="bg-zinc-900/40 p-8 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Tag className="w-5 h-5 text-[#bd6aff]" />
                  </div>
                  <h2 className="text-xl font-black uppercase tracking-tighter text-white">
                    Labour Rest Area <span className="text-purple-500">Sale</span>
                  </h2>
                </div>

                <p className="text-zinc-400 text-xs leading-relaxed mb-8">
                  Labour Rest Area for construction site tents and industrial hubs. Fully customizable structures designed to meet UAE labor law requirements for worker comfort.
                </p>

                <div className="mt-auto">
                  <ul className="space-y-3 mb-8">
                    {[
                      "3m - 60m Clear Span Widths",
                      "Integrated HVAC & AC Ducting",
                      "2.5m, 3m, 4m & 6m Eave Height Options",
                      "Compliant with Civil Defence",
                      "UV-Reflective Heat Insulation",
                      "Fire-Retardant & UV Protection",
                      "Engineered for UAE Wind Loads",
                      "Washing & Sanitary Block Add-ons",
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-[11px] text-zinc-300">
                        <CheckCircle2 className="w-4 h-4 text-[#bd6aff]" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <button onClick={scrollToQuote} className="w-full px-6 py-4 bg-white/5 hover:bg-[#bd6aff] border border-white/10 hover:border-[#bd6aff] rounded-xl text-white text-[10px] font-black uppercase tracking-widest transition-all duration-300 flex justify-center items-center gap-2 group">
                    Get Quote <Tag className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                  </button>
                </div>
              </div>
            </div>

            <div className="relative group p-[1px] rounded-3xl overflow-hidden bg-white/5 border border-white/10 flex flex-col">
              <div className="bg-zinc-900/40 p-8 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Clock className="w-5 h-5 text-[#bd6aff]" />
                  </div>
                  <h2 className="text-xl font-black uppercase tracking-tighter text-white">
                    Labour Rest Area <span className="text-purple-500">Rental</span>
                  </h2>
                </div>

                <p className="text-zinc-400 text-xs leading-relaxed mb-8">
                  Fast deployment for seasonal overflow, logistics projects, or temporary inventory needs. Scalable structures available for immediate site installation.
                </p>

                <div className="mt-auto">
                  <ul className="space-y-3 mb-8">
                    {[
                      "3m - 60m Rental Inventory",
                      "Rapid Installation & Removal",
                      "24-Hour Maintenance Support",
                      "Pre-Installed HVAC, Lighting & Power",
                      "Flexible Monthly Lease Terms",
                      "Insulated Wall Options Available",
                      "Full Structural Maintenance",
                      "Expandable Accommodation Capacity",
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-[11px] text-zinc-300">
                        <CheckCircle2 className="w-4 h-4 text-[#bd6aff]" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <button onClick={scrollToQuote} className="w-full px-6 py-4 bg-white/5 hover:bg-[#bd6aff] border border-white/10 hover:border-[#bd6aff] rounded-xl text-white text-[10px] font-black uppercase tracking-widest transition-all duration-300 flex justify-center items-center gap-2 group">
                    Get Quote <Clock className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                  </button>
                </div>
              </div>
            </div>

            <div className="relative group p-[1px] rounded-3xl overflow-hidden bg-gradient-to-b from-[#bd6aff]/20 to-transparent border border-[#bd6aff]/30 lg:col-span-1 md:col-span-2">
              <div className="bg-zinc-950/80 p-8 h-full flex flex-col">
                <h2 className="text-lg font-black uppercase tracking-tight text-white mb-6 text-center">
                  Rental vs Sale Comparison
                </h2>

                {/* Changed space-y-5 to space-y-1 to keep the header and rows tighter */}
                <div className="space-y-1">
                  <div className="grid grid-cols-3 text-[10px] font-bold uppercase tracking-widest text-zinc-300 pb-2 border-b border-white/5">
                    <div>Feature</div>
                    <div className="text-center">Rental</div>
                    <div className="text-center">Sale</div>
                  </div>

                  {[
                    { f: "Short-Term Use", r: true, s: true },
                    { f: "Long-Term ROI", r: false, s: true },
                    { f: "Lightings", r: "Incl.", s: "Opt." },
                    { f: "Carpets", r: "Incl.", s: "Opt." },
                    { f: "Air Conditioning", r: "Incl.", s: "Opt." },
                    { f: "Tables & Chairs", r: "Incl.", s: "Opt." },
                    { f: "Wooden Floopring", r: "Opt.", s: "Opt." },
                    { f: "Maintenance", r: "Incl.", s: "Opt." },
                    { f: "Lead Time", r: "Fast", s: "Custom & Fast" },
                    { f: "Setup Speed", r: "24-8hrs", s: "Planned" },
                  ].map((row, i) => (
                    /* Changed py-4 to py-2 to reduce the inner row gap */
                    <div key={i} className="grid grid-cols-3 items-center py-2 border-t border-white/5">
                      <div className="text-[10px] font-bold text-zinc-300 uppercase leading-none">{row.f}</div>
                      <div className="flex justify-center">
                        {row.r === true ? <Check className="w-4 h-4 text-[#bd6aff]" /> : row.r === false ? <X className="w-4 h-4 text-zinc-600" /> : <span className="text-[9px] text-[#bd6aff] font-bold uppercase">{row.r}</span>}
                      </div>
                      <div className="flex justify-center">
                        {row.s === true ? <Check className="w-4 h-4 text-[#bd6aff]" /> : row.s === false ? <X className="w-4 h-4 text-zinc-600" /> : <span className="text-[9px] text-[#bd6aff] font-bold uppercase">{row.s}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>



        {/* --- MOHRE COMPLIANCE SECTION --- */}
<section className="pb-24 max-w-7xl mx-auto px-6">
  <div className="relative overflow-hidden rounded-[2rem] bg-[#0F0F0F] border border-white/10 p-6 md:p-10 shadow-2xl">

    {/* Background Glow */}
    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple-600/10 blur-[140px] rounded-full pointer-events-none" />

    <div className="relative z-10">

      {/* Header */}
      <div className="max-w-5xl mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-purple-500/30 bg-purple-500/10 mb-5">
          <BadgeCheck className="w-4 h-4 text-[#bd6aff]" />
          <span className="text-[10px] font-black uppercase tracking-widest text-[#bd6aff]">
            MOHRE Midday Break Compliance
          </span>
        </div>

        <h2 className="text-3xl md:text-5xl font-black tracking-tight uppercase text-white leading-[1.05] mb-5">
          MOHRE-Compliant Midday Break{" "}
          <span className="text-[#bd6aff]">Labour Rest Tents</span> in UAE
        </h2>

        <div className="space-y-4 text-zinc-300 text-sm leading-relaxed">
          <p>
            The UAE Ministry of Human Resources and Emiratisation (MOHRE)
            requires all companies employing outdoor workers to provide approved
            midday break labour tents for rest in UAE during the official summer
            break period from{" "}
            <span className="text-white font-bold">12:30 PM to 3:00 PM</span>,
            effective annually from{" "}
            <span className="text-white font-bold">15 June to 15 September</span>.
          </p>

          <p>
            Failure to comply with MOHRE labour welfare regulations,
            construction site safety requirements, and worker rest area
            standards in UAE can lead to substantial fines, labour violations,
            and project shutdowns.
          </p>

          <p>
            As a leading manufacturer and supplier of MOHRE-compliant labour
            rest area tents in UAE, BA Tents provides high-quality construction
            labour shelter tents, temporary worker rest area tents, industrial
            shade tents, and labour welfare tents in UAE on rental and sale
            designed specifically for harsh GCC weather conditions and UAE
            labour law compliance.
          </p>

          <p>
            BA Tents delivers durable, weather-resistant, and fully compliant
            labour rest area shade solutions in UAE that improve worker comfort,
            safety, and site compliance.
          </p>
        </div>
      </div>

      {/* Top Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          {
            icon: Thermometer,
            title: "Heat & UV Protection",
            desc: "Heat-resistant and UV-protected fabrics for UAE summer conditions.",
          },
          {
            icon: ShieldCheck,
            title: "Fire-Retardant Materials",
            desc: "Certified fire-retardant tent systems with enhanced safety compliance.",
          },
          {
            icon: Zap,
            title: "Cooling Ready",
            desc: "Compatible with portable AC units, coolers, and industrial fans.",
          },
          {
            icon: Users,
            title: "Worker Welfare Layouts",
            desc: "Designed for workforce comfort, ventilation, and site welfare standards.",
          },
        ].map((item, i) => (
          <div
            key={i}
            className="p-5 rounded-2xl bg-zinc-900/60 border border-white/5 hover:border-[#bd6aff]/40 transition-all"
          >
            <item.icon className="w-7 h-7 text-[#bd6aff] mb-4" />

            <h3 className="text-white text-xs font-black uppercase mb-2">
              {item.title}
            </h3>

            <p className="text-zinc-400 text-[11px] leading-relaxed">
              {item.desc}
            </p>
          </div>
        ))}
      </div>

      {/* Main Grid */}
      <div className="grid lg:grid-cols-[1.8fr_1fr] gap-5">

        {/* Left Side */}
        <div className="p-6 rounded-3xl bg-white/[0.03] border border-white/5 flex flex-col h-full">

          {/* Compliance Features */}
          <div>
            <h3 className="text-2xl font-black uppercase tracking-tight text-white mb-5">
              MOHRE Compliance <span className="text-[#bd6aff]">Features</span>
            </h3>

            <p className="text-zinc-400 text-xs leading-relaxed mb-5">
              Our MOHRE-approved labour shelter tents are engineered to meet all
              UAE labour welfare and outdoor worker safety standards.
            </p>

            <div className="grid md:grid-cols-2 gap-3">
              {[
                "Large shaded labour rest areas with proper airflow and ventilation",
                "Heat-resistant and UV-protected tent fabrics",
                "Fire-retardant materials with certified safety compliance",
                "Structural stability against wind loads and sandstorms",
                "Compatibility with portable AC units, air coolers, and industrial fans",
                "Adequate seating arrangements based on workforce capacity",
                "Designated drinking water station areas",
                "Durable aluminium and galvanized steel tent structures",
                "Fast installation and relocation for temporary construction projects",
                "Heavy-duty flooring and weatherproof sidewalls available",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 p-3 rounded-xl bg-black/20 border border-white/5"
                >
                  <CheckCircle2 className="w-4 h-4 text-[#bd6aff] shrink-0 mt-0.5" />

                  <p className="text-zinc-300 text-[11px] leading-relaxed">
                    {item}
                  </p>
                </div>
              ))}
            </div>

            <p className="text-zinc-400 text-xs leading-relaxed mt-5">
              Our labour cooling tents in UAE are ideal for maintaining worker
              welfare during extreme summer temperatures while ensuring complete
              compliance with UAE midday break regulations.
            </p>
          </div>

          {/* Why Choose Section - fills empty space */}
          <div className="mt-5 pt-5 border-t border-white/10">
            <h3 className="text-xl font-black uppercase tracking-tight text-white mb-4">
              Why Choose <span className="text-[#bd6aff]">BA Tents?</span>
            </h3>

            <div className="grid md:grid-cols-2 gap-3">
              {[
                "Trusted UAE-based tent manufacturer and supplier",
                "Specialised in MOHRE-compliant labour welfare shelters",
                "Heavy-duty industrial-grade tent materials",
                "Custom-designed construction site rest area tents",
                "Fast production, delivery, and installation across UAE",
                "Suitable for temporary and long-term workforce use",
                "Weatherproof and heat-resistant shelter systems",
                "Competitive pricing for bulk labour tent projects",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 p-3 rounded-xl bg-black/20 border border-white/5"
                >
                  <CheckCircle2 className="w-4 h-4 text-[#bd6aff] shrink-0 mt-0.5" />

                  <p className="text-zinc-300 text-[10px] leading-relaxed">
                    {item}
                  </p>
                </div>
              ))}
            </div>

            <p className="text-zinc-400 text-xs leading-relaxed mt-5">
              Protect your workforce and ensure full compliance with UAE labour
              laws using BA Tents MOHRE-compliant labour rest tents,
              construction shade shelters, and worker welfare tent solutions in
              UAE designed for maximum durability, comfort, and safety.
            </p>
          </div>
        </div>

        {/* Right Side */}
        <div className="p-6 rounded-3xl bg-gradient-to-b from-[#bd6aff]/10 to-transparent border border-[#bd6aff]/20">

          {/* Labour Welfare Solutions */}
          <h3 className="text-2xl font-black uppercase text-white mb-5">
            Labour Welfare Solutions
          </h3>

          <div className="space-y-2 mb-6">
            {[
              "MOHRE-Compliant Labour Rest Tents",
              "Midday Break Shade Tents in UAE",
              "Construction Worker Shelter Tents",
              "Industrial Cooling Tents in UAE",
              "Portable Workforce Rest Tents",
              "Outdoor Staff Welfare Shelters",
              "Labour Camp Shade Structures",
              "Heavy-Duty PVC Labour Rest Tents",
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-2 text-[11px] text-zinc-300"
              >
                <CheckCircle2 className="w-4 h-4 text-[#bd6aff] shrink-0 mt-0.5" />
                {item}
              </div>
            ))}
          </div>

          {/* UAE Coverage */}
          <div className="border-t border-white/10 pt-5">
            <h4 className="text-lg font-black uppercase text-white mb-4">
              UAE Coverage
            </h4>

            <div className="space-y-3">
              {[
                "Dubai",
                "Abu Dhabi",
                "Sharjah",
                "Ajman",
                "Ras Al Khaimah",
                "Fujairah",
                "Umm Al Quwain",
                "Al Ain",
              ].map((city, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 text-[10px] font-bold uppercase text-zinc-300"
                >
                  <MapPin className="w-4 h-4 text-[#bd6aff]" />
                  Labour Tent Rental & Sale in {city}
                </div>
              ))}
            </div>
          </div>

          {/* Bottom Tags */}
          <div className="grid grid-cols-2 gap-3 mt-6">
            {[
              "Rental & Sale",
              "Fast Installation",
              "Custom Sizes",
              "Cooling Systems",
              "MOHRE Compliant",
              "UAE-Wide Coverage",
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-2 p-3 rounded-xl bg-white/5 border border-white/5"
              >
                <CheckCircle2 className="w-4 h-4 text-[#bd6aff]" />

                <span className="text-[10px] font-bold uppercase text-zinc-300">
                  {item}
                </span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <button
            onClick={scrollToQuote}
            className="mt-6 w-full py-4 bg-[#bd6aff] hover:bg-purple-500 rounded-xl text-white text-[10px] font-black uppercase tracking-widest transition-all"
          >
            Request MOHRE Tent Quote
          </button>
        </div>
      </div>
    </div>
  </div>
</section>



        {/* --- FAQ SECTION --- */}
<section className="pb-32 max-w-5xl mx-auto px-6 relative">
  {/* Subtle Background Glow to break the blackness */}
  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/5 blur-[120px] rounded-full pointer-events-none" />

  <div className="text-center mb-12 relative z-10">
    <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-4 uppercase text-white">
      Labour Rest Area Tent <span className="text-[#bd6aff]">FAQ</span>
    </h2>
    <p className="text-zinc-500 text-[10px] uppercase tracking-[0.3em]">Workforce Welfare Insights</p>
  </div>

  <div className="space-y-4 relative z-10">
    {[
      {
        q: "Do you provide labour rest area tents on a rental basis in the UAE?",
        a: (
          <div className="space-y-4">
            <p>Yes. <span className="text-white font-bold">BA Tents</span> provides flexible labour rest area tent rental in UAE, worker break room rentals in Dubai, and temporary workforce welfare solutions for construction, industrial, and infrastructure projects across all Emirates.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                <p className="text-[#bd6aff] font-bold text-[10px] uppercase mb-2">Ideal for:</p>
                <ul className="space-y-1 text-[11px] text-zinc-400">
                  {["Construction sites", "On-site dining areas", "Worker cooling stations", "Site induction zones"].map(i => <li key={i}>• {i}</li>)}
                </ul>
              </div>
              <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                <p className="text-[#bd6aff] font-bold text-[10px] uppercase mb-2">Rental Features:</p>
                <ul className="space-y-1 text-[11px] text-zinc-400">
                  {["Integrated AC systems", "Welfare compliance", "Rapid site setup", "Flexible lease terms"].map(i => <li key={i}>• {i}</li>)}
                </ul>
              </div>
            </div>
          </div>
        )
      },
      {
        q: "Do you offer labour rest area tents for sale in the UAE?",
        a: "Yes. BA Tents is a leading labour rest area manufacturer in UAE, providing high-quality workforce cooling tents for sale and permanent site welfare structures with factory-direct pricing. We handle everything from site planning to structural installation."
      },
      {
        q: "What is the difference between labour rest area rental and purchase?",
        a: "Rental is perfect for temporary construction phases where fast deployment and manageable costs are key. Purchase is more suitable for permanent industrial facilities or long-term infrastructure developments where a fixed welfare asset is required."
      },
      {
        q: "Can rest area tents be customized for specific workforce sizes?",
        a: (
          <div className="space-y-2">
            <p>Yes. Our modular designs allow us to scale for small site crews or thousands of workers. Customization options include:</p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-zinc-400">
              <li>• Insulated wall panels</li>
              <li>• Heavy-duty AC units</li>
              <li>• Prayer room partitions</li>
              <li>• Dining furniture kits</li>
            </ul>
          </div>
        )
      },
      {
        q: "Do you provide installation and maintenance for on-site welfare tents?",
        a: "Yes. BA Tents provides full installation, site preparation, and ongoing maintenance support across Dubai, Abu Dhabi, Sharjah, and all other Emirates to ensure the rest area remains safe and cooling systems are optimized."
      },
      {
        q: "Are these rest area tents engineered for high-heat UAE summers?",
        a: "Absolutely. Our labour rest areas are designed specifically for the UAE climate, featuring heat-resistant PVC fabrics, fire-retardant materials, and high-performance HVAC systems to maintain safe, comfortable temperatures even in 50°C+ conditions."
      }
    ].map((item, idx) => (
      <div key={idx} className="group p-[1px] rounded-2xl bg-white/10 border border-white/5 hover:border-[#bd6aff]/40 transition-all duration-300 shadow-xl">
        <details className="group">
          <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
            <span className="text-sm font-bold text-zinc-200 uppercase tracking-tight group-hover:text-white transition-colors">{item.q}</span>
            <Plus className="w-4 h-4 text-[#bd6aff] group-open:rotate-45 transition-transform duration-300" />
          </summary>
          <div className="px-6 pb-6 text-zinc-400 text-xs leading-relaxed border-t border-white/5 pt-4">
            {item.a}
          </div>
        </details>
      </div>
    ))}
  </div>
</section>
      </div>

      <FloatingContactBar onQuoteClick={scrollToQuote} />
      <Footer />
    </PageLayout>
  );
};

export default Warehousing;